import React, { useState } from 'react';
import { format } from 'date-fns';
import { CalendarButton } from '../../ui/CalendarButton';

function ChevronRightIcon({ className = '' }) {
  return (
    <svg className={className} width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9.47 5.47a.75.75 0 0 1 1.06 0l6 6a.75.75 0 0 1 0 1.06l-6 6a.75.75 0 1 1-1.06-1.06L14.94 12 9.47 6.53a.75.75 0 0 1 0-1.06Z" />
    </svg>
  );
}

function normalizeMemberTags(rawTags) {
  if (Array.isArray(rawTags)) {
    return rawTags.filter((tag) => typeof tag === 'string' && tag.trim() !== '').map((tag) => tag.trim());
  }
  if (rawTags && typeof rawTags === 'object') {
    return Object.entries(rawTags)
      .filter(([name, enabled]) => Boolean(enabled) && typeof name === 'string' && name.trim() !== '')
      .map(([name]) => name.trim());
  }
  return [];
}

function hasTagOverlap(allowedTags, memberTags) {
  if (!Array.isArray(allowedTags) || allowedTags.length === 0) return true;
  const mine = new Set(memberTags || []);
  return allowedTags.some((tag) => mine.has(tag));
}

function getEligibleVacantRoleSummary(event, memberTags, isAdmin) {
  const roles = Array.isArray(event?.roles) ? event.roles : [];
  if (roles.length === 0) return [];

  return roles
    .map((role) => {
      const allowedTags = Array.isArray(role?.allowedTags) ? role.allowedTags : [];
      const eligible = isAdmin || hasTagOverlap(allowedTags, memberTags);
      if (!eligible) return null;

      const slots = Array.isArray(role?.slots) ? role.slots : [];
      const vacantCount = slots.filter((slot) => !slot?.assigneeUserId).length;
      if (vacantCount < 1) return null;

      return {
        name: typeof role?.name === 'string' && role.name.trim() !== '' ? role.name.trim() : 'Role',
        count: vacantCount,
      };
    })
    .filter(Boolean);
}

export default function EventsCard({
  events = [],
  onOpenCalendar,
  onOpenEvent,
  onOpenEventSetup,
  isAdmin,
  memberTags = [],
  publicEventsSlug = '',
  canCheckIn = false,
  teamId = '',
}) {
  const [copiedPublicEvent, setCopiedPublicEvent] = useState('');
  const memberTagNames = normalizeMemberTags(memberTags);
  const copyPublicEventLink = async evt => {
    if (!publicEventsSlug || !evt?.publicSlug || !navigator?.clipboard) return;
    const occurrence = evt.occurrenceId ? `?occurrence=${encodeURIComponent(evt.occurrenceId)}` : '';
    const path = `/events/${publicEventsSlug}/${evt.publicSlug}${occurrence}`;
    await navigator.clipboard.writeText(`${window.location.origin}${path}`);
    const key = `${evt.publicSlug}:${evt.occurrenceId || ''}`;
    setCopiedPublicEvent(key);
    window.setTimeout(() => setCopiedPublicEvent(current => current === key ? '' : current), 2200);
  };

  // 1) Normalize each event into { …evt, start: Date }
  const normalized = events
    .map(evt => {
      let start = null;

      if (evt.start instanceof Date) {
        start = evt.start;
      }
      else if (evt.start?.toDate) {
        start = evt.start.toDate();
      }
      else if (typeof evt.start === 'string') {
        start = new Date(evt.start);
      }
      // A) If you already supplied an ISO string
      else if (typeof evt.startDateTime === 'string') {
        start = new Date(evt.startDateTime);
      }
      // B) If you have a Firestore Timestamp plus optional time
      else if (evt.date?.toDate) {
        start = evt.date.toDate();
        if (evt.time && typeof evt.time === 'string') {
          const [h, m] = evt.time.split(':');
          start.setHours(+h, +m);
        }
      }
      // C) If you have raw date+time strings
      else if (evt.date && evt.time) {
        start = new Date(`${evt.date}T${evt.time}`);
      }

      // If we couldn’t build a valid Date, drop this event
      if (!(start instanceof Date) || isNaN(start)) return null;
      return { ...evt, start };
    })
    .filter(Boolean);

  // 2) Filter to future, sort, and take next 5
  const upcoming = normalized
    .filter(evt => evt.start >= new Date())
    .sort((a, b) => a.start - b.start)
    .slice(0, 5);

  return (
    <div className="team-card events-card">
      <div className="card-header-with-icon">
        <span className="card-title">Events</span>
        <div className="events-card__header-actions">
          <CalendarButton
            collapseLabel
            onClick={onOpenCalendar}
            aria-label="Open full events calendar"
            title="Open full events calendar"
          >
            Calendar
          </CalendarButton>

          {isAdmin && (
            <button
              type="button"
              className="edit-btn"
              onClick={onOpenEventSetup}
              aria-label="Manage event setup"
              title="Manage event setup"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M19.14 12.94a7.77 7.77 0 0 0 .05-.94 7.77 7.77 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.06 7.06 0 0 0-1.63-.94l-.36-2.54a.5.5 0 0 0-.49-.42h-3.84a.5.5 0 0 0-.49.42l-.36 2.54c-.58.23-1.12.54-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.7 8.84a.5.5 0 0 0 .12.64l2.03 1.58a7.77 7.77 0 0 0-.05.94c0 .32.02.63.05.94L2.82 14.52a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .6.22l2.39-.96c.5.4 1.05.71 1.63.94l.36 2.54a.5.5 0 0 0 .49.42h3.84a.5.5 0 0 0 .49-.42l.36-2.54a7.06 7.06 0 0 0 1.63-.94l2.39.96a.5.5 0 0 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="card-body events-card__body">
        {upcoming.length > 0 ? (
          <ul className="events-card__list" aria-label="Upcoming events">
            {upcoming.map(evt => (
              <li key={evt.id} className="events-card__item">
                {(() => {
                  const eligibleVacancies = getEligibleVacantRoleSummary(
                    evt,
                    memberTagNames,
                    isAdmin
                  );
                  return (
                    <div className="events-card__event-shell">
                      <button
                        type="button"
                        className="events-card__event-button"
                        onClick={() => onOpenEvent?.(evt)}
                      >
                        <span className="events-card__date-badge" aria-hidden="true">
                          <span>{format(evt.start, 'MMM')}</span>
                          <strong>{format(evt.start, 'd')}</strong>
                        </span>
                        <span className="events-card__event-content">
                          <span className="events-card__event-name">{evt.name}</span>
                          <span className="events-card__event-meta">
                            {format(evt.start, 'eee d MMM yyyy • HH:mm')}
                            {evt.mode === 'inPerson' && evt.location?.name && (
                              <> • {evt.location.name}</>
                            )}
                          </span>

                          {!isAdmin && eligibleVacancies.length > 0 && (
                            <span className="events-card__vacancies">
                              Vacant roles you can cover:{' '}
                              {eligibleVacancies
                                .map((entry) => `${entry.name}${entry.count > 1 ? ` (${entry.count})` : ''}`)
                                .join(', ')}
                            </span>
                          )}
                        </span>
                        <span className="events-card__open-cue">
                          <span>Open</span>
                          <ChevronRightIcon />
                        </span>
                      </button>
                      {evt.mode === 'online' && evt.onlineUrl && (
                        <a
                          href={evt.onlineUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="events-card__online-link"
                        >
                          Join online
                        </a>
                      )}
                      {isAdmin && publicEventsSlug && evt.allowNonMembers && evt.publicSlug && (() => {
                        const shareKey = `${evt.publicSlug}:${evt.occurrenceId || ''}`;
                        return <button type="button" className="events-card__public-link" onClick={() => copyPublicEventLink(evt)}>
                          {copiedPublicEvent === shareKey ? 'Public link copied' : 'Copy public link'}
                        </button>;
                      })()}
                      {canCheckIn && teamId && evt.attendanceMode === 'ticketed' && evt.admissionTicketsEnabled === true && <a className="events-card__public-link" href={`/teams/${teamId}/events/${evt.originalId || evt.id}/check-in${evt.occurrenceId ? `?occurrence=${encodeURIComponent(evt.occurrenceId)}` : ''}`}>Open check-in</a>}
                    </div>
                  );
                })()}
              </li>
            ))}
          </ul>
        ) : (
          <p className="events-card__empty">No upcoming events.</p>
        )}
      </div>
    </div>
  );
}
